import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGamepad, faCoins, faGem, faShare } from "@fortawesome/free-solid-svg-icons";

import { useCurrentUser } from "../hooks/contexts";
import { useColorScheme } from "../hooks/telegram";
import fullLogo from "../assets/images/full-icon-dark.svg";

export default function FullPageLoader() {
  const { isLoading } = useCurrentUser();
  const { toggleTopBarColor } = useColorScheme();
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    toggleTopBarColor();
    
    if (isLoading) {
      // Show loader immediately when loading starts
      setShowLoader(true);
      setProgress(0);
      
      const start = Date.now();
      const minDuration = 3000; // Minimum 3 seconds
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - start;
        const percent = Math.min((elapsed / minDuration) * 100, 100);
        setProgress(percent);
        
        // Only clear when both progress is complete AND isLoading is false
        if (percent >= 100 && !isLoading) {
          clearInterval(interval);
          // Add a small delay before hiding to ensure smooth transition
          setTimeout(() => setShowLoader(false), 200);
        }
      }, 50); // Smoother updates
      
      return () => {
        clearInterval(interval);
      };
    } else if (progress >= 100) {
      // If loading finishes but progress isn't complete, wait for progress to finish
      setTimeout(() => setShowLoader(false), 200);
    }
  }, [toggleTopBarColor, isLoading]);

  // Keep loader visible until both loading is done AND progress is complete
  if (!showLoader) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="loader"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
        className="fixed inset-0 bg-white dark:bg-gray-900 flex flex-col items-center justify-center z-50 p-6"
      >
        <motion.div 
          className="flex-1 flex flex-col items-center justify-center w-full max-w-md mx-auto"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
        >
          <motion.div 
            className="relative mb-12"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 400 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 rounded-full blur-3xl"></div>
            <img
              src={fullLogo}
              alt="Fameverse Logo"
              className="relative w-48 h-48 animate-float drop-shadow-2xl" 
              draggable="false"
            />
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-2 gap-4 mb-12 w-full"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <motion.div 
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center mb-3">
                <FontAwesomeIcon icon={faGamepad} className="text-xl text-primary" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Play</span>
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-xl flex items-center justify-center mb-3">
                <FontAwesomeIcon icon={faCoins} className="text-xl text-secondary" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Earn</span>
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-accent/20 to-accent/10 rounded-xl flex items-center justify-center mb-3">
                <FontAwesomeIcon icon={faGem} className="text-xl text-accent" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Mine</span>
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/10 rounded-xl flex items-center justify-center mb-3">
                <FontAwesomeIcon icon={faShare} className="text-xl text-primary" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Share</span>
            </motion.div>
          </motion.div>

          <motion.div 
            className="w-full text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <div className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-6">
              Loading your experience...
            </div>
            
            <div className="relative w-full max-w-xs mx-auto">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: 'easeOut', duration: 0.2 }}
                  className="h-full bg-gradient-to-r from-primary via-secondary to-accent rounded-full shadow-lg"
                />
              </div>
              
              <motion.div 
                className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-400"
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {Math.floor(progress)}%
              </motion.div>
              
              <div className="flex justify-center mt-4 space-x-1">
                <motion.div 
                  className="w-2 h-2 bg-primary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                />
                <motion.div 
                  className="w-2 h-2 bg-secondary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div 
                  className="w-2 h-2 bg-accent rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
